// Vendor attribution for the Cost section.
//
// None of the services store a "vendor" field: cost is recorded against the
// endpoint (or model) that produced it. This module is the single place that
// maps those keys onto the party we actually pay, so the "By service" and
// "Merch Cost" breakdowns agree on what a vendor is.
//
// Key shapes we classify:
//   - `fal_usage` keys are endpoint ids (`fal-ai/…`, `bytedance/…`,
//     `uveye/…`, `openrouter/router/vision`). All of these are billed by FAL,
//     including the OpenRouter passthrough FAL hosts.
//   - `fal_usage` also carries direct (non-FAL) image edits, keyed
//     `{provider}_{model}` by the backend — e.g. `openai_gpt_image_2`,
//     `azure_gpt_image_2`. Those are billed by OpenAI / Microsoft.
//   - `gemini_usage` keys are Gemini/Vertex model names, billed by Google.
//   - gen-api records the provider tier that served each call (`fal`,
//     `vertex`, `openai`, `azure`, `local`).

export type VendorKey =
  | 'fal'
  | 'google_ai'
  | 'openai'
  | 'azure'
  | 'gcp'
  | 'taggers'
  | 'inhouse'
  | 'other';

export const VENDOR_LABELS: Record<VendorKey, string> = {
  fal: 'FAL',
  google_ai: 'Google AI (Vertex)',
  openai: 'OpenAI',
  azure: 'Azure OpenAI',
  gcp: 'Google Cloud (GCP)',
  taggers: 'Taggers',
  inhouse: 'In-process',
  other: 'Unattributed',
};

export const VENDOR_COLORS: Record<VendorKey, string> = {
  fal: '#6366f1',
  google_ai: '#10b981',
  openai: '#0ea5e9',
  azure: '#8b5cf6',
  gcp: '#f59e0b',
  taggers: '#14b8a6',
  inhouse: '#94a3b8',
  other: '#cbd5e1',
};

/** Stable display order so a vendor keeps its position as costs shift. */
const VENDOR_ORDER: VendorKey[] = [
  'fal',
  'google_ai',
  'openai',
  'azure',
  'gcp',
  'taggers',
  'inhouse',
  'other',
];

/**
 * Vendor behind a `fal_usage` key. Anything with a `/` is an endpoint id FAL
 * bills us for; the `{provider}_{model}` form marks a direct provider call the
 * backend folded into the same map.
 */
export function vendorForUsageEndpoint(endpoint: string): VendorKey {
  const key = endpoint.toLowerCase();
  if (key.startsWith('azure_') || key.startsWith('azure-')) return 'azure';
  if (key.startsWith('openai_') || key.startsWith('openai-')) return 'openai';
  if (key.startsWith('vertex_') || key.startsWith('gemini_')) return 'google_ai';
  if (key.includes('/')) return 'fal';
  return 'other';
}

/** Vendor behind a `gemini_usage` model name — always Google. */
export function vendorForGeminiModel(): VendorKey {
  return 'google_ai';
}

/** Vendor behind a gen-api `api_calls.provider` value. */
export function vendorForApigenProvider(provider: string | null | undefined): VendorKey {
  switch ((provider || '').toLowerCase()) {
    case 'fal':
      return 'fal';
    case 'vertex':
    case 'gemini':
    case 'google':
      return 'google_ai';
    case 'openai':
      return 'openai';
    case 'azure':
      return 'azure';
    case 'local':
      return 'inhouse';
    default:
      return 'other';
  }
}

/** A vendor's share of some cost total, with the keys rolled into it. */
export interface VendorCost {
  vendor: VendorKey;
  cost: number;
  /** Billable calls attributed to this vendor. 0 when the source has no count. */
  calls: number;
  /** Endpoints / models / components rolled up here, for tooltips. */
  detail: string[];
}

/** Accumulates cost per vendor, keeping the contributing keys for tooltips. */
export class VendorTally {
  private readonly byVendor = new Map<VendorKey, { cost: number; calls: number; detail: Set<string> }>();

  add(vendor: VendorKey, cost: number, calls = 0, detail?: string): void {
    let entry = this.byVendor.get(vendor);
    if (!entry) {
      entry = { cost: 0, calls: 0, detail: new Set<string>() };
      this.byVendor.set(vendor, entry);
    }
    entry.cost += cost;
    entry.calls += calls;
    if (detail) entry.detail.add(detail);
  }

  /**
   * Vendors with a non-zero contribution, ordered by cost (descending) and
   * tie-broken by `VENDOR_ORDER`. Vendors that only ever saw $0 and no calls
   * are dropped so the breakdown stays free of empty rows.
   */
  toList(): VendorCost[] {
    return Array.from(this.byVendor.entries())
      .filter(([, v]) => v.cost > 0 || v.calls > 0)
      .map(([vendor, v]) => ({
        vendor,
        cost: v.cost,
        calls: v.calls,
        detail: Array.from(v.detail).sort(),
      }))
      .sort((a, b) => b.cost - a.cost || VENDOR_ORDER.indexOf(a.vendor) - VENDOR_ORDER.indexOf(b.vendor));
  }
}

/** Merge several vendor breakdowns into one (used for cross-service totals). */
export function mergeVendorCosts(lists: VendorCost[][]): VendorCost[] {
  const tally = new VendorTally();
  for (const list of lists) {
    for (const v of list) {
      tally.add(v.vendor, v.cost, v.calls);
      for (const d of v.detail) tally.add(v.vendor, 0, 0, d);
    }
  }
  return tally.toList();
}
